import { useFlags } from 'launchdarkly-react-client-sdk'
import { useMemo } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown'
import clsx from 'clsx'

import { DEVELOPER_DASHBOARD_URL, FEATURE_FLAGS } from '@infura/packages.common/constants'
import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { isEnterpriseUser } from '@infura/packages.common/user'
import { Button } from '@infura/packages.ui/Button'
import { Spinner } from '@infura/packages.ui/Spinner'
import { ServiceTier } from '@infura/packages.uks'

import { RolesDropdown, RolesValue } from 'components/RolesDropdown'

import { useUKSProjects } from 'hooks/useUKSProjects'
import { useUKSServicePlan } from 'hooks/useUKSServicePlan'

interface Props {
  role: RolesValue
  setRole: (role: RolesValue) => void
  toggleCreateModalOpen: () => void
  toggleUpgradePlanModalOpen: () => void
}

export const KeyServiceToolbar: React.FC<Props> = ({
  role,
  setRole,
  toggleCreateModalOpen,
  toggleUpgradePlanModalOpen
}) => {
  const flags = useFlags()
  const { servicePlan } = useUKSServicePlan()
  const { projects: _projects } = useUKSProjects()
  const projects = Object.values(_projects || {})
  const isNewAPIKeyPolicyEnabled = useMemo(() => flags[FEATURE_FLAGS.NEW_API_KEY_POLICY_ENABLED], [flags])
  const isPlanInfoReady = servicePlan && projects
  const isNeededToUpgrade =
    isPlanInfoReady &&
    !isEnterpriseUser(servicePlan) &&
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    (servicePlan.tier === ServiceTier.CORE ? projects.length >= 1 : projects.length >= servicePlan.limits.projectsCap)

  return (
    <div className='mt-2 flex flex-wrap gap-2 md:mt-0'>
      <RolesDropdown setRole={setRole} value={role} />
      <Button
        className='group mt-2 flex w-full justify-center sm:mt-0 sm:w-auto sm:justify-start'
        data-testid='create-project'
        onClick={() => {
          if (isNewAPIKeyPolicyEnabled && isNeededToUpgrade) {
            toggleUpgradePlanModalOpen()
          } else {
            toggleCreateModalOpen()
          }
          trackClickForSegmentAnalytics({
            clickText: `Create new API key`,
            clickUrl: `${DEVELOPER_DASHBOARD_URL}`,
            location: 'Header',
            type: 'Text'
          })
        }}
        theme='secondary'
      >
        {isNewAPIKeyPolicyEnabled ? (
          <span
            className={clsx(
              'mb-0.5 mr-2 flex h-[18px] w-[18px] items-center justify-center',
              isNeededToUpgrade && 'rounded-full bg-yellow-600'
            )}
            data-testid={isNeededToUpgrade ? 'gold-crown' : 'grey-crown'}
          >
            {isPlanInfoReady ? (
              <FontAwesomeIcon
                className={clsx(
                  'text-[10px] group-hover:text-current',
                  isNeededToUpgrade ? 'text-white' : 'text-gray-400'
                )}
                icon={faCrown}
              />
            ) : (
              <Spinner className='h-full w-full' />
            )}
          </span>
        ) : null}
        <span>Create new API key</span>
      </Button>
    </div>
  )
}
